import React, { useMemo } from 'react'
import { Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { TableData, TitledTableData, buildDataTableObject } from 'utils/data'
import {
    setFirstColumnInHeaders,
    transformLaggingData,
    transformTotalData,
    transformLeadingData,
} from './helpers'
import {
    useSafetyStoreLagging,
    useSafetyStoreTotal,
    useSafetyStoreLeading,
} from 'hooks/safety/useSafetyStore'
import { BasicPlaceholder } from 'components/placeholder'
import useRefresh from 'hooks/utils/useRefresh'
import useCountdown from 'hooks/utils/useCountdown'
import { ErrorAlert } from 'components/error-fallback'
import { translateTitledTableData } from 'locales/utils'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'

const emptyTable: TableData = { headings: [], rows: [] }

export const SafetyStore: React.FC = () => {
    const { t, currentLocation, env, logout, session } = useEssentials()

    const laggingResult = useSafetyStoreLagging({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const totalResult = useSafetyStoreTotal({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const leadingResult = useSafetyStoreLeading({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    useRefresh(
        () => {
            void laggingResult.refetch()
            void totalResult.refetch()
            void leadingResult.refetch()
        },
        () => {
            laggingResult.remove()
            totalResult.remove()
            leadingResult.remove()
        },
    )

    const count = useCountdown(
        5,
        logout,
        (laggingResult.error ||
            totalResult.error ||
            leadingResult.error) as Error,
    )

    const laggingTable = useMemo(() => {
        const t = transformLaggingData(laggingResult.data ?? emptyTable)
        return {
            ...t,
            headings: setFirstColumnInHeaders(t, 'timeframe'),
        }
    }, [laggingResult.data])

    const totalTable = useMemo(() => {
        const t = transformTotalData(totalResult.data ?? emptyTable)
        return {
            ...t,
            headings: setFirstColumnInHeaders(t, 'timeframe'),
        }
    }, [totalResult.data])

    const leadingTable = useMemo(() => {
        const t = transformLeadingData(leadingResult.data ?? emptyTable)
        return {
            ...t,
            headings: setFirstColumnInHeaders(t, 'indicator'),
        }
    }, [leadingResult.data])

    const { safety } = TRANSLATION_KEYS
    const { store: storeKeys } = safety

    const laggingTitled = useMemo<TitledTableData>(() => {
        return translateTitledTableData({
            t,
            data: { title: 'Lagging Indicators', table: laggingTable },
            translation: storeKeys,
        })
    }, [t, laggingTable, storeKeys])

    const totalTitled = useMemo<TitledTableData>(() => {
        return translateTitledTableData({
            t,
            data: { title: 'Total Incidents', table: totalTable },
            translation: storeKeys,
        })
    }, [t, totalTable, storeKeys])

    const leadingTitled = useMemo<TitledTableData>(() => {
        return translateTitledTableData({
            t,
            data: { title: 'Leading Indicators', table: leadingTable },
            translation: storeKeys,
        })
    }, [t, leadingTable, storeKeys])

    if (
        laggingResult.isLoading ||
        totalResult.isLoading ||
        leadingResult.isLoading
    ) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }

    if (laggingResult.isError || totalResult.isError || leadingResult.isError) {
        return (
            <ErrorAlert
                error={
                    laggingResult.error ||
                    totalResult.error ||
                    leadingResult.error
                }
                count={count}
                onRetry={() => {
                    void laggingResult.refetch()
                    void totalResult.refetch()
                    void leadingResult.refetch()
                }}
            />
        )
    }

    return (
        <div>
            {[
                { data: laggingResult.data, titled: laggingTitled },
                { data: totalResult.data, titled: totalTitled },
                { data: leadingResult.data, titled: leadingTitled },
            ].map(({ data, titled }, idx) => {
                const { title, table } = titled

                const hasData = data?.rows && data.rows.length > 0

                return (
                    <Grid.Container
                        className="hc8-pv-4x"
                        key={`${title}-${idx}-${currentLocation}`}
                    >
                        <Grid.Item
                            className="hc8-pb-none"
                            size={{
                                xs: 12,
                            }}
                        >
                            <p className="hc8-pa-1x hc8-lh-body">{title}</p>
                        </Grid.Item>
                        <Grid.Item
                            className="hc8-pt-none"
                            size={{
                                xs: 12,
                            }}
                        >
                            {hasData ? (
                                <Table
                                    key={`${title}-${currentLocation}-${idx}`}
                                    data={buildDataTableObject({
                                        currentData: table,
                                        widths: [],
                                    })}
                                    name={title}
                                />
                            ) : (
                                <ErrorAlert variant="no-data" />
                            )}
                        </Grid.Item>
                    </Grid.Container>
                )
            })}
        </div>
    )
}
